import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import useSeo from "../lib/useSeo";
import {
  HERO,
  BUSINESS_SERVICES,
  PERSONAL_SERVICES,
  WHY_CHOOSE,
  IMAGES,
} from "../lib/content";

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "LegalService",
  name: "IntoLaw",
  legalName: "IntoLaw, a Division of Praxio (Pty) Ltd",
  description:
    "Independent South African legal consultancy offering affordable legal consulting, compliance solutions, document drafting and dispute-resolution support.",
  url: "https://intolaw.co.za",
  email: "intolaw@praxio.co.za",
  areaServed: "ZA",
  address: {
    "@type": "PostalAddress",
    streetAddress: "31 5th Street, Commercia",
    addressLocality: "Midrand",
    postalCode: "1685",
    addressCountry: "ZA",
  },
};

export default function Home() {
  useSeo({
    title: "Accessible Legal Solutions",
    description: HERO.subheading,
    path: "/",
    jsonLd: orgJsonLd,
  });

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <>
      {/* ---------------- HERO ---------------- */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden px-6 pt-32 pb-16 lg:px-10 lg:pt-40">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.8 }}
              className="mb-8 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-gold"
            >
              <span className="h-px w-10 bg-gold" /> Independent Legal Consultancy
            </motion.p>

            <h1 className="font-display text-5xl font-semibold leading-[0.95] tracking-tight text-navy sm:text-6xl lg:text-[5.5rem]">
              <MaskedLines lines={HERO.headline} />
            </h1>

            <Reveal delay={0.9} className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 lg:text-lg">
              {HERO.subheading}
            </Reveal>

            <Reveal delay={1.05} className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/contact"
                data-testid="hero-contact-cta"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 font-medium text-white transition-colors duration-300 hover:bg-royal"
              >
                Contact Us
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/20 px-8 py-4 font-medium text-navy transition-colors duration-300 hover:bg-navy hover:text-white"
              >
                Explore Our Services
              </Link>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative mx-auto aspect-[3/4] w-full max-w-md overflow-hidden arch-frame shadow-[0_30px_60px_rgba(10,17,40,0.18)]"
            >
              <motion.img
                src={IMAGES.hero}
                alt="Modern legal consulting workspace"
                loading="eager"
                style={{ y: imgY, scale: imgScale }}
                className="h-full w-full object-cover"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---------------- MARQUEE ---------------- */}
      <section className="border-y border-navy/10 bg-[#FDFBF7] py-6" aria-hidden="true">
        <Marquee speed={40} gradient={false} autoFill>
          {["Praxio (Pty) Ltd", "Modern Legal Strategy", "Compliance", "Contract Drafting", "Mediation", "POPIA", "Consulting"].map(
            (t, i) => (
              <span key={i} className="mx-8 inline-flex items-center gap-8 font-display text-2xl text-navy lg:text-3xl">
                {t} <span className="text-gold">✦</span>
              </span>
            )
          )}
        </Marquee>
      </section>

      {/* ---------------- SERVICES OVERVIEW (BENTO) ---------------- */}
      <section className="px-6 py-24 lg:px-10 lg:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-16 max-w-2xl">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-gold">What we do</p>
            <h2 className="font-display text-4xl font-medium leading-tight tracking-tight text-navy lg:text-5xl">
              Practical legal support, built for businesses and individuals alike.
            </h2>
          </Reveal>

          <div className="grid gap-6 md:grid-cols-12">
            <Reveal className="md:col-span-8">
              <div className="group relative h-full min-h-[320px] overflow-hidden rounded-2xl border border-black/5">
                <img
                  src={IMAGES.brass}
                  alt="Business legal solutions"
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-navy/70" />
                <div className="relative flex h-full flex-col justify-end p-8 lg:p-12">
                  <span className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-gold">Business Services · B2B</span>
                  <h3 className="font-display text-3xl font-medium text-white lg:text-4xl">{BUSINESS_SERVICES.title}</h3>
                  <p className="mt-3 max-w-lg text-white/70">{BUSINESS_SERVICES.intro}</p>
                  <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-gold hover:gap-3 transition-all duration-300">
                    Explore business services <ArrowUpRight size={18} />
                  </Link>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="md:col-span-4">
              <div className="flex h-full flex-col justify-between rounded-2xl bg-navy p-8 text-white lg:p-10">
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Personal · B2C</span>
                <div>
                  <h3 className="mt-6 font-display text-3xl font-medium">{PERSONAL_SERVICES.title}</h3>
                  <p className="mt-3 text-white/70">{PERSONAL_SERVICES.intro}</p>
                  <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-gold hover:gap-3 transition-all duration-300">
                    Explore personal services <ArrowUpRight size={18} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------- WHY CHOOSE (MANIFESTO) ---------------- */}
      <section className="relative overflow-hidden bg-navy grain px-6 py-24 lg:px-10 lg:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-16 max-w-2xl">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-gold">Why choose IntoLaw</p>
            <h2 className="font-display text-4xl font-medium leading-tight tracking-tight text-white lg:text-5xl">
              A modern approach to legal consulting.
            </h2>
          </Reveal>

          <div className="divide-y divide-white/10 border-t border-white/10">
            {WHY_CHOOSE.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.05}>
                <div className="group grid grid-cols-1 items-start gap-4 py-8 md:grid-cols-12 md:gap-8">
                  <div className="md:col-span-2">
                    <span className="font-display text-5xl font-medium text-stroke transition-colors duration-300 group-hover:text-gold group-hover:[-webkit-text-stroke:0]">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl font-medium text-white md:col-span-4 lg:text-3xl">{item.title}</h3>
                  <p className="text-white/70 md:col-span-6 lg:text-lg">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="px-6 py-24 lg:px-10 lg:py-32">
        <Reveal className="mx-auto max-w-4xl text-center">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-gold">Ready when you are</p>
          <h2 className="font-display text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl lg:text-6xl">
            Legal confidence, without the traditional price tag.
          </h2>
          <Link
            to="/contact"
            data-testid="cta-contact-button"
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-gold px-9 py-4 font-medium text-navy transition-colors duration-300 hover:bg-goldDark"
          >
            Start your enquiry
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
